import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import { BookingSummaryMonth, HttpError } from "../../../api/types";
import useShowToast from "../../../hooks/useShowToast";

/**
 * Custom hook to fetch the booking summary from backend.
 * @param {string} dateFrom - Start date in YYYY-MM-DD format
 * @param {string} dateTo - End date in YYYY-MM-DD format
 * @returns {object} - Contains summary data, loading state, error state, and refetch function.
 */
function useFetchBookingSummary() {
  const { t } = useTranslation();
  const [bookingSummary, setBookingSummary] = useState<BookingSummaryMonth[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HttpError | null>(null);
  const { errorToast } = useShowToast();

  const fetchSummary = async (dateFrom: Date, dateTo: Date) => {
    if (!dateFrom || !dateTo) {
      setBookingSummary([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const summary = await api.getBookingSummary(dateFrom, dateTo);

      if (api.error) {
        setError(api.error);
        errorToast({
          title: t("club_management.bookings.fetch.error_title"),
          description: t("club_management.bookings.fetch.error_description"),
        });
      }

      setBookingSummary(summary?.months || []);
    } finally {
      setLoading(false);
    }
  };

  return {
    bookingSummary,
    loading,
    error,
    refetchSummary: fetchSummary,
  };
}

export default useFetchBookingSummary;
